import React, { useState } from "react";
import { KeyRound, UserCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import TeamAccess from "../../components/admin/TeamAccess";
import { authApi, errorMessage } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const inputCls = "h-11 rounded-none border-border bg-transparent px-3 text-sm focus-visible:ring-1 focus-visible:ring-primary focus-visible:ring-offset-0";

const AccountCard = ({ user }) => (
  <section className="border border-border bg-card" data-testid="account-card">
    <header className="flex items-center gap-2 border-b border-border px-6 py-4 text-sm font-semibold">
      <UserCircle2 className="h-4 w-4 text-primary" /> Your account
    </header>
    <div className="flex flex-wrap items-center gap-4 px-6 py-5">
      {user?.picture ? (
        <img src={user.picture} alt="" className="h-12 w-12 rounded-full border border-border" referrerPolicy="no-referrer" />
      ) : (
        <span className="flex h-12 w-12 items-center justify-center border border-border text-lg font-semibold uppercase">{(user?.name || user?.username || "?").slice(0, 1)}</span>
      )}
      <div>
        <p className="text-sm font-semibold" data-testid="account-name">{user?.name || user?.username}</p>
        <p className="text-xs text-muted-foreground">{user?.email || `Username: ${user?.username}`}</p>
      </div>
      <div className="ml-auto flex gap-2 text-[10px] font-semibold uppercase tracking-[0.2em]">
        <span className="border border-primary/50 px-2.5 py-1 text-primary" data-testid="account-role">{user?.role === "master" ? "Master admin" : "Team member"}</span>
        <span className="border border-border px-2.5 py-1 text-muted-foreground" data-testid="account-provider">{user?.auth_provider === "google" ? "Google sign-in" : "Password"}</span>
      </div>
    </div>
  </section>
);

const ChangePassword = () => {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 8) return toast.error("New password must be at least 8 characters");
    if (form.next !== form.confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await authApi.changePassword(form.current, form.next);
      toast.success("Password updated");
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5 border border-border bg-card p-6" data-testid="change-password-form">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <KeyRound className="h-4 w-4 text-primary" /> Change password
      </div>
      <div className="space-y-1.5">
        <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Current password</Label>
        <Input type="password" value={form.current} onChange={(e) => setForm((f) => ({ ...f, current: e.target.value }))} className={inputCls} data-testid="settings-current-password" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">New password</Label>
          <Input type="password" value={form.next} onChange={(e) => setForm((f) => ({ ...f, next: e.target.value }))} className={inputCls} data-testid="settings-new-password" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Confirm</Label>
          <Input type="password" value={form.confirm} onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))} className={inputCls} data-testid="settings-confirm-password" />
        </div>
      </div>
      <Button type="submit" disabled={busy} className="h-11 rounded-none px-6 text-[11px] font-semibold uppercase tracking-[0.2em]" data-testid="settings-submit">
        {busy ? "Updating…" : "Update password"}
      </Button>
    </form>
  );
};

const AdminSettings = () => {
  const { user, isMaster } = useAuth();
  return (
    <div className="max-w-3xl space-y-8" data-testid="admin-settings">
      <AdminPageHeader crumbs={[{ label: "Settings" }]} eyebrow="Account" title="Settings" description="Your account, who can sign in with Google, and your password." />
      <AccountCard user={user} />
      <TeamAccess canManage={isMaster} />
      {user?.auth_provider !== "google" && <ChangePassword />}
    </div>
  );
};

export default AdminSettings;
